import logger from "utils/logger";
/**
 * Premium calculation cache with TTL (Time To Live)
 * Stores premium results keyed by risk object hash
 * Auto-expires entries after 5 minutes
 * 
 * Cache structure:
 * {
 *   [riskHash]: {
 *     data: { gross_premium, tax_amount, service_charge, total_amount_due, ... },
 *     timestamp: Date.now(),
 *     ttl: 300000 (5 minutes in ms)
 *   }
 * }
 */

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY_PREFIX = 'agic_premium_cache_';

/**
 * Generates a stable hash from risk object for cache key
 * Only includes fields that affect premium calculation
 * 
 * @param {Object} risk - Risk object from buildNewRiskObject
 * @returns {string} - Hash of normalized risk data
 */
export function generateRiskHash(risk) {
    if (!risk) {
        return null;
    }

    // Extract only fields that affect premium calculation
    // Exclude fields like risk_id, timestamps, user info that don't affect premium
    const relevantFields = {
        // Vehicle identifiers
        chassisNo: risk.chassisNo || risk.chassis_no,
        engineNo: risk.engineNo || risk.engine_no,
        registrationNumber: risk.registrationNumber || risk.registration_number,
        
        // Vehicle details affecting premium
        vehicleYear: risk.vehicleYear || risk.year,
        vehicleMake: risk.vehicleMake || risk.make,
        vehicleModel: risk.vehicleModel || risk.model,
        bodyType: risk.bodyType || risk.body_type,
        fuelType: risk.fuel_type,
        transmissionType: risk.transmission_type,
        seatsNo: risk.seatsNo || risk.number_of_seats,
        ccRating: risk.ccRating || risk.cc_rating,
        ladenWeight: risk.ladenWeight || risk.laden_weight,
        
        // Coverage affecting premium
        estimatedMarketValue: risk.estimatedMarketValue || risk.sum_insured,
        coverageCode: risk.coverageCode || risk.coverage_code,
        ratingCode: risk.ratingCode || risk.rating_code,
        
        // Mortgagee affects premium
        hasMortgagee: risk.has_mortgagee,
        mortgageeName: risk.mortgagee_name,
        
        // Policy context
        policyId: risk.policy_id,
        isChangeVehicle: risk.isChangeVehicle,
        oldRiskId: risk.oldRiskId,
    };

    // Sort keys for consistent hash regardless of object property order
    const sortedKeys = Object.keys(relevantFields).sort();
    const normalizedString = sortedKeys
        .map(key => `${key}:${relevantFields[key]}`)
        .join('|');

    // Simple hash function (djb2 algorithm)
    let hash = 5381;
    for (let i = 0; i < normalizedString.length; i++) {
        hash = ((hash << 5) + hash) + normalizedString.charCodeAt(i);
    }
    
    return `${CACHE_KEY_PREFIX}${hash.toString(36)}`;
}

/**
 * Retrieves cached premium data if available and not expired
 * 
 * @param {string} riskHash - Hash generated from generateRiskHash
 * @returns {Object|null} - Cached premium data or null if expired/not found
 */
export function getCachedPremium(riskHash) {
    if (!riskHash) {
        return null;
    }

    try {
        const cacheEntry = sessionStorage.getItem(riskHash);
        
        if (!cacheEntry) {
            if (process.env.NODE_ENV === 'development') {
                logger.log('[Premium Cache] Cache miss', { riskHash });
            }
            return null;
        }

        const { data, timestamp, ttl } = JSON.parse(cacheEntry);
        const age = Date.now() - timestamp;

        // Check if cache entry has expired
        if (age > ttl) {
            if (process.env.NODE_ENV === 'development') {
                logger.log('[Premium Cache] Cache expired', {
                    riskHash,
                    ageMinutes: (age / 60000).toFixed(2),
                    ttlMinutes: (ttl / 60000).toFixed(2)
                });
            }
            
            // Clean up expired entry
            sessionStorage.removeItem(riskHash);
            return null;
        }

        if (process.env.NODE_ENV === 'development') {
            logger.log('[Premium Cache] Cache hit', {
                riskHash,
                ageSeconds: (age / 1000).toFixed(1),
                totalAmountDue: data.total_amount_due
            });
        }

        return data;
    } catch (error) {
        logger.error('[Premium Cache] Error reading cache', { error: error.message, riskHash });
        return null;
    }
}

/**
 * Stores premium calculation result in cache
 * 
 * @param {string} riskHash - Hash generated from generateRiskHash
 * @param {Object} premiumData - Response from addRiskToPolicy or changeRiskOnPolicy
 * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
 */
export function setCachedPremium(riskHash, premiumData, ttl = CACHE_TTL_MS) {
    if (!riskHash || !premiumData) {
        return;
    }

    try {
        const cacheEntry = {
            data: premiumData,
            timestamp: Date.now(),
            ttl
        };

        sessionStorage.setItem(riskHash, JSON.stringify(cacheEntry));

        if (process.env.NODE_ENV === 'development') {
            logger.log('[Premium Cache] Cached premium', {
                riskHash,
                ttlMinutes: (ttl / 60000).toFixed(2),
                totalAmountDue: premiumData.total_amount_due
            });
        }
    } catch (error) {
        // sessionStorage might be full or disabled
        logger.warn('[Premium Cache] Failed to cache premium', {
            error: error.message,
            riskHash
        });
    }
}

/**
 * Clears all premium cache entries (useful for logout or policy changes)
 */
export function clearPremiumCache() {
    try {
        const keysToRemove = [];
        
        // Find all keys with our prefix
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }

        // Remove them
        keysToRemove.forEach(key => sessionStorage.removeItem(key));

        if (process.env.NODE_ENV === 'development') {
            logger.log('[Premium Cache] Cleared cache', {
                entriesCleared: keysToRemove.length
            });
        }
    } catch (error) {
        logger.error('[Premium Cache] Error clearing cache', { error: error.message });
    }
}

/**
 * Clears expired cache entries (garbage collection)
 * Call periodically or on component mount
 */
export function cleanExpiredCacheEntries() {
    try {
        const keysToRemove = [];
        const now = Date.now();

        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (!key || !key.startsWith(CACHE_KEY_PREFIX)) {
                continue;
            }

            try {
                const cacheEntry = JSON.parse(sessionStorage.getItem(key));
                const age = now - cacheEntry.timestamp;
                
                if (age > cacheEntry.ttl) {
                    keysToRemove.push(key);
                }
            } catch (parseError) {
                // Corrupted entry, remove it
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach(key => sessionStorage.removeItem(key));

        if (process.env.NODE_ENV === 'development' && keysToRemove.length > 0) {
            logger.log('[Premium Cache] Cleaned expired entries', {
                entriesRemoved: keysToRemove.length
            });
        }
    } catch (error) {
        logger.error('[Premium Cache] Error cleaning cache', { error: error.message });
    }
}

/**
 * Gets cache statistics for debugging
 * @returns {Object} - Cache stats (size, entries, oldest entry age)
 */
export function getCacheStats() {
    try {
        let totalEntries = 0;
        let totalSize = 0;
        let oldestAge = 0;
        const now = Date.now();

        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (!key || !key.startsWith(CACHE_KEY_PREFIX)) {
                continue;
            }

            const value = sessionStorage.getItem(key);
            totalSize += key.length + value.length;
            totalEntries++;

            try {
                const cacheEntry = JSON.parse(value);
                const age = now - cacheEntry.timestamp;
                if (age > oldestAge) {
                    oldestAge = age;
                }
            } catch (e) {
                // Skip corrupted entries
            }
        }

        return {
            totalEntries,
            totalSizeKB: (totalSize / 1024).toFixed(2),
            oldestEntryAgeMinutes: (oldestAge / 60000).toFixed(2)
        };
    } catch (error) {
        return { error: error.message };
    }
}